import { ArrowRightIcon } from '@radix-ui/react-icons'
import * as Tabs from '@radix-ui/react-tabs'
import { useState } from 'react'
import { Link, useSearchParams } from 'react-router'
import { match } from 'ts-pattern'
import { AnalysisStatusBadge } from '../components/badges/analysis-status-badge.tsx'
import { TriageCompositionBar } from '../components/composition-bar'
import { PaginationControls } from '../components/table/pagination-controls'
import { Pagination } from '../components/table/table'
import { BranchName } from '../components/tags/branch-name'
import { typography } from '../main.css.ts'
import { Routes } from '../routes.ts'
import { useGetAllAnalysesWithRepositories, useGetFindingsPerAnalysis } from '../utils/api-client/api-hooks.ts'
import { PaginatedFindings, Repository, ScanAnalysis } from '../utils/api-client/api-schemas.ts'
import { useTheme } from '../utils/higher-order-components/with-theme'
import { getRepositoryIcon } from '../utils/repository-utils.tsx'
import { mapToolToLogoAndName } from './analysis-details-page'
import * as styles from './scans-overview-page.css.ts'

export function ScansOverviewPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const activeTab = searchParams.get('tab') === 'in-progress' ? 'in-progress' : 'latest'

  const [pagination, setPagination] = useState<Pagination>({
    pageIndex: 0,
    pageSize: 10,
  })

  const {
    baseResult: { data: paginatedInProgressAnalyses, isLoading: inProgressLoading },
    linkedResults: inProgressLinkedResults,
  } = useGetAllAnalysesWithRepositories({
    states: ['queued', 'in_progress'],
    pageSize: pagination.pageSize,
    pageIndex: activeTab === 'in-progress' ? pagination.pageIndex : 0,
  })

  const {
    baseResult: { data: paginatedCompletedAnalyses, isLoading: completedLoading },
    linkedResults: completedLinkedResults,
  } = useGetAllAnalysesWithRepositories({
    states: ['completed'],
    pageSize: pagination.pageSize,
    pageIndex: activeTab === 'latest' ? pagination.pageIndex : 0,
  })

  const isLoading = (activeTab === 'in-progress' && inProgressLoading) || (activeTab === 'latest' && completedLoading)

  const handlePaginationChange = (newPagination: Pagination) => {
    setPagination(newPagination)
  }

  const completedFindings = useGetFindingsPerAnalysis({
    analyses: paginatedCompletedAnalyses?._embedded.items ?? [],
  })
    .map(result => result.data)
    .filter((f): f is PaginatedFindings => !!f)

  return (
    <ScansOverviewPagePure
      isLoading={isLoading}
      inProgressAnalyses={paginatedInProgressAnalyses?._embedded.items ?? []}
      inProgressRepositories={inProgressLinkedResults.map(r => r.data).filter((r): r is Repository => !!r)}
      completedAnalyses={paginatedCompletedAnalyses?._embedded.items ?? []}
      completedRepositories={completedLinkedResults.map(r => r.data).filter((r): r is Repository => !!r)}
      completedFindings={completedFindings}
      activeTab={activeTab}
      onTabChange={tab => {
        setSearchParams({ tab })
        setPagination({ pageIndex: 0, pageSize: pagination.pageSize }) // Reset page when changing tabs
      }}
      pagination={pagination}
      onPaginationChange={handlePaginationChange}
      completedTotal={paginatedCompletedAnalyses?.total ?? 0}
      inProgressTotal={paginatedInProgressAnalyses?.total ?? 0}
    />
  )
}

export type ScansOverviewPagePureProps = {
  isLoading: boolean
  completedAnalyses: ScanAnalysis[]
  inProgressAnalyses: ScanAnalysis[]
  completedRepositories: Repository[]
  inProgressRepositories: Repository[]
  completedFindings: PaginatedFindings[]
  activeTab: 'latest' | 'in-progress'
  onTabChange: (tab: 'latest' | 'in-progress') => void
  pagination: Pagination
  onPaginationChange: (pagination: Pagination) => void
  completedTotal: number
  inProgressTotal: number
}

export const ScansOverviewPagePure: React.FC<ScansOverviewPagePureProps> = ({
  activeTab,
  onTabChange,
  completedTotal,
  inProgressTotal,
  completedAnalyses,
  inProgressAnalyses,
  completedRepositories,
  inProgressRepositories,
  completedFindings,
  pagination,
  onPaginationChange,
  isLoading,
}) => {
  if (isLoading)
    return (
      <div className={styles.scansOverviewBody}>
        <ScansOverviewHeader />
        <div className={styles.loadingContainer}>Loading scans...</div>
      </div>
    )

  return (
    <div className={styles.scansOverviewBody}>
      <ScansOverviewHeader />
      <Tabs.Root value={activeTab} onValueChange={value => onTabChange(value as 'latest' | 'in-progress')} className={styles.tabContainer}>
        <Tabs.List className={styles.tabList}>
          <Tabs.Trigger className={styles.tab} value="latest">
            LATEST &nbsp;<span className={styles.tabItemCount}>{completedTotal}</span>
          </Tabs.Trigger>
          <Tabs.Trigger className={styles.tab} value="in-progress">
            IN PROGRESS &nbsp;<span className={styles.tabItemCount}>{inProgressTotal}</span>
          </Tabs.Trigger>
        </Tabs.List>

        <Tabs.Content className={styles.tabContent} value="latest">
          <CompletedAnalysesTabContent
            isLoading={isLoading}
            completedAnalyses={completedAnalyses}
            completedFindings={completedFindings}
            completedRepositories={completedRepositories}
          />
        </Tabs.Content>

        <Tabs.Content className={styles.tabContent} value="in-progress">
          <InProgressAnalysesTabContent
            isLoading={isLoading}
            inProgressAnalyses={inProgressAnalyses}
            inProgressRepositories={inProgressRepositories}
          />
        </Tabs.Content>
      </Tabs.Root>
      <div className={styles.paginationContainer}>
        {match(activeTab)
          .with('latest', () => (
            <PaginationControls
              tableLabel="Completed Scans"
              pageIndex={pagination.pageIndex}
              pageSize={pagination.pageSize}
              rowCount={completedTotal}
              setPageIndex={pageIndex => onPaginationChange({ pageIndex, pageSize: pagination.pageSize })}
              setPageSize={pageSize => onPaginationChange({ pageIndex: pagination.pageIndex, pageSize })}
            />
          ))
          .with('in-progress', () => (
            <PaginationControls
              tableLabel="In Progress Scans"
              pageIndex={pagination.pageIndex}
              pageSize={pagination.pageSize}
              rowCount={inProgressTotal}
              setPageIndex={pageIndex => onPaginationChange({ pageIndex, pageSize: pagination.pageSize })}
              setPageSize={pageSize => onPaginationChange({ pageIndex: pagination.pageIndex, pageSize })}
            />
          ))
          .exhaustive()}
      </div>
    </div>
  )
}

const ScansOverviewHeader = () => (
  <div role="banner" className={styles.scansOverviewHeader}>
    <h4 className={`${typography.h4} ${styles.headerTitle}`}>Scans</h4>
    <p className={`${typography.paragraph} ${styles.headerParagraph}`}>Upload, triage and fix your SAST scan findings.</p>
  </div>
)

type CompletedAnalysesTabContentProps = {
  isLoading: boolean
  completedAnalyses: ScanAnalysis[]
  completedFindings: PaginatedFindings[]
  completedRepositories: Repository[]
}

const CompletedAnalysesTabContent: React.FC<CompletedAnalysesTabContentProps> = ({
  isLoading,
  completedAnalyses,
  completedFindings,
  completedRepositories,
}) => {
  if (!isLoading && completedAnalyses.length === 0)
    return <p className={styles.noScansMessage}>No scans found. Please upload tool results to a connected repository.</p>

  return (
    <>
      {completedAnalyses.map((analysis, index) => (
        <ScanArticle key={analysis.id} analysis={analysis} findings={completedFindings[index]} repository={completedRepositories[index]} />
      ))}
    </>
  )
}

type InProgressAnalysesTabContentProps = {
  isLoading: boolean
  inProgressAnalyses: ScanAnalysis[]
  inProgressRepositories: Repository[]
}

const InProgressAnalysesTabContent: React.FC<InProgressAnalysesTabContentProps> = ({ isLoading, inProgressAnalyses, inProgressRepositories }) => {
  if (!isLoading && inProgressAnalyses.length === 0) return <p className={styles.noScansMessage}>No scans in progress.</p>

  return (
    <>
      {inProgressAnalyses.map((analysis, index) => (
        <ScanArticle key={analysis.id} analysis={analysis} repository={inProgressRepositories[index]} />
      ))}
    </>
  )
}

type ScanArticleProps = {
  analysis: ScanAnalysis
  findings?: PaginatedFindings
  repository?: Repository
}

const ScanArticle: React.FC<ScanArticleProps> = ({ analysis, findings, repository }) => {
  const { theme } = useTheme()

  const date = new Date(analysis.current_state.timestamp)
  const formattedDate = date.toLocaleDateString('en-US', {
    month: 'numeric',
    day: 'numeric',
    year: '2-digit',
  })
  const formattedTime = date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  })

  const RepositoryIcon = getRepositoryIcon({ type: repository?.type ?? 'git', className: styles.scmIcon })

  const { logoHref, name } = mapToolToLogoAndName(analysis._embedded.scan.detector, theme)

  return (
    <article aria-label={`Scan for ${analysis._embedded.scan._links.repository.title}`} className={styles.scanArticle}>
      <div className={styles.scanContainer} data-testid="scan-container">
        <div className={styles.firstCell}>
          <div className={styles.toolLogoContainer}>
            <img src={logoHref} alt={`${name} logo`} className={styles.toolLogo} />
          </div>
          <p className={styles.date}>{formattedDate}</p>
          <p className={styles.time}>{formattedTime}</p>
          {analysis._embedded.scan.branch && <BranchName branch={analysis._embedded.scan.branch} />}
        </div>
        <div className={styles.mainContent}>
          <div className={styles.repositoryInfo}>
            <div className={styles.repositoryHeader}>
              {RepositoryIcon}
              <p className={styles.repositoryName}>{analysis._embedded.scan._links.repository.title}</p>
              {analysis.current_state.state && analysis.current_state.state !== 'completed' && (
                <AnalysisStatusBadge variant={analysis.current_state.state} />
              )}
            </div>
            <div className={styles.triageCompositionBarContainer}>
              {findings && 'completed' === analysis.current_state.state ? (
                <TriageCompositionBar
                  fixes={findings.fixes}
                  total={findings.total}
                  falsePositives={findings.false_positives}
                  truePositives={findings.true_positives}
                  suspicious={findings.suspicious}
                  wontFix={findings.wont_fix}
                  tool={analysis._embedded.scan.detector}
                  analysisState={analysis.current_state.state}
                  compact
                />
              ) : analysis.current_state.state === 'in_progress' || analysis.current_state.state === 'queued' ? (
                <TriageCompositionBar
                  fixes={0}
                  total={0}
                  falsePositives={0}
                  truePositives={0}
                  suspicious={0}
                  wontFix={0}
                  tool={analysis._embedded.scan.detector}
                  analysisState={analysis.current_state.state}
                  compact
                />
              ) : null}
            </div>
          </div>
        </div>
        <div className={styles.lastCell}>
          <Link to={Routes.AnalysisDetailsPage.createPath({ analysisId: analysis.id })} className={styles.analysisLink}>
            View analysis
            <ArrowRightIcon />
          </Link>
        </div>
      </div>
    </article>
  )
}
